import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AdminSidebar, AdminMobileHeader } from '@/components/AdminSidebar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from '@/lib/api';
import { toast } from 'sonner';

export default function AdminMarcas() {
  const [marcas, setMarcas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMarca, setEditingMarca] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({ nombre: '' });

  const fetchMarcas = async () => {
    setLoading(true);
    try {
      const data = await api.getMarcas();
      setMarcas(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching marcas:', error);
      toast.error('Error al cargar las marcas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMarcas(); }, []);

  const openCreateDialog = () => {
    setEditingMarca(null);
    setFormData({ nombre: '' });
    setIsDialogOpen(true);
  };

  const openEditDialog = (marca) => {
    setEditingMarca(marca);
    setFormData({ nombre: marca.nombre });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    try {
      if (editingMarca) {
        await api.updateMarca(editingMarca.id, formData);
        toast.success('Marca actualizada exitosamente');
      } else {
        await api.createMarca(formData);
        toast.success('Marca creada exitosamente');
      }
      setIsDialogOpen(false);
      fetchMarcas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar la marca');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.deleteMarca(deleteId);
      toast.success('Marca eliminada exitosamente');
      fetchMarcas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar la marca');
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebar active="marcas" />

      <main className="lg:ml-64 p-6 md:p-8">
        <AdminMobileHeader />

        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <Tag className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold uppercase tracking-wider">Gestion de Marcas</h1>
              <p className="text-muted-foreground">Administra las marcas de vehiculos del inventario</p>
            </div>
          </div>
          <Button onClick={openCreateDialog} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Marca
          </Button>
        </div>

        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Nombre</TableHead>
                <TableHead className="text-muted-foreground">Slug</TableHead>
                <TableHead className="text-muted-foreground text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(4)].map((_, i) => (
                  <TableRow key={i} className="border-border">
                    <TableCell colSpan={3}><div className="h-12 bg-muted animate-pulse rounded" /></TableCell>
                  </TableRow>
                ))
              ) : marcas.length > 0 ? (
                marcas.map((marca) => (
                  <TableRow key={marca.id} className="border-border">
                    <TableCell className="font-medium">{marca.nombre}</TableCell>
                    <TableCell className="text-muted-foreground">{marca.slug}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(marca)} title="Editar">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteId(marca.id)} className="text-destructive hover:text-destructive" title="Eliminar">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-12 text-muted-foreground">No hay marcas registradas</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </main>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>{editingMarca ? 'Editar Marca' : 'Nueva Marca'}</DialogTitle>
            <DialogDescription>
              {editingMarca ? 'Modifica el nombre de la marca' : 'Crea una nueva marca de vehiculo'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ nombre: e.target.value })}
                placeholder="Ej: Toyota"
                className="bg-input border-transparent focus:border-primary"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="btn-primary">{editingMarca ? 'Actualizar' : 'Crear'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar marca?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion no se puede deshacer. La marca sera eliminada permanentemente.
              <br /><br />
              <strong>Nota:</strong> No se pueden eliminar marcas que tengan vehiculos asignados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
